<template>
    <Card pageTitle="Quantity Adjustments">
        <div>
            <AdjustmentTabs />
            <div>
                <div class="d-flex justify-content-between align-items-center">
                    <div class="mb-3"><strong>Quantity Adjustments</strong></div>
                    <div class="d-flex" style="gap: 10px">
                        <!-- <Button noMargin @click="flushData">{{
                            flushing ? "Please wait..." : "Clear All"
                        }}</Button> -->
                        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
                            <router-link to="/inventory-adjustments/quantity/create">
                                <Button noMargin>Create Qty Adjustment</Button>
                            </router-link>
                        </AllowPermission>
                    </div>
                </div>
                <p class="mb-1">Search By ID</p>
                <form @submit.prevent="fetchQtyAdjustments">
                    <div class="d-flex mb-3" style="max-width: 400px; gap: 15px">
                        <CustomInput class="flex-1" small type="text" v-model="search" />
                        <Button :condensed="true" noMargin>Search</Button>
                    </div>
                </form>
            </div>
            <div v-if="loading" class="loading-container">
                <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                </div>
                <p class="mt-2">Loading quantity adjustments...</p>
            </div>
            <AllowPermission :permission="PERMISSION_CODES.CMS_VIEW_ADJUSTMENT">
                <div class="adjustments-table-container">
                    <div class="table-responsive">
                        <table class="custom-adjustments-table">
                            <thead>
                                <tr>
                                    <th class="adjustment-id-col">
                                        <i class="fas fa-tag mr-2"></i>Adjustment ID
                                    </th>
                                    <th class="items-col">
                                        <i class="fas fa-box mr-2"></i>Items
                                    </th>
                                    <th class="increase-col">
                                        <i class="fas fa-arrow-up mr-2 text-success"></i>Cost Increase
                                    </th>
                                    <th class="decrease-col">
                                        <i class="fas fa-arrow-down mr-2 text-danger"></i>Cost Decrease
                                    </th>
                                    <th class="net-change-col">
                                        <i class="fas fa-balance-scale mr-2"></i>Net Change
                                    </th>
                                    <th class="date-col">
                                        <i class="fas fa-calendar mr-2"></i>Date Created
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="adst of qtyAdjustments" :key="adst.id" class="adjustment-row">
                                    <td class="adjustment-id-cell">
                                        <LinkComponent :to="`/inventory-adjustments/quantity/${adst.name}`" class="adjustment-link">
                                            <div class="adjustment-id-content">
                                                <span class="adjustment-name">{{ adst.name }}</span>
                                                <span class="adjustment-type">Quantity Adjustment</span>
                                            </div>
                                        </LinkComponent>
                                    </td>
                                    <td class="items-cell">
                                        <div class="items-badge">
                                            {{ adst.totalItems }}
                                            <span class="items-text">{{ adst.totalItems === 1 ? 'item' : 'items' }}</span>
                                        </div>
                                    </td>
                                    <td class="increase-cell">
                                        <div class="cost-value positive" v-if="adst.costIncrease > 0">
                                            <span class="currency">$</span>{{ formatCurrency(adst.costIncrease) }}
                                        </div>
                                        <div class="cost-value neutral" v-else>
                                            <span class="currency">$</span>0.00
                                        </div>
                                    </td>
                                    <td class="decrease-cell">
                                        <div class="cost-value negative" v-if="adst.costDecrease > 0">
                                            <span class="currency">$</span>{{ formatCurrency(adst.costDecrease) }}
                                        </div>
                                        <div class="cost-value neutral" v-else>
                                            <span class="currency">$</span>0.00
                                        </div>
                                    </td>
                                    <td class="net-change-cell">
                                        <div class="net-change-container">
                                            <div class="net-value" :class="getNetChangeClass(adst.netChange)">
                                                <i :class="getNetChangeIcon(adst.netChange)" class="mr-1"></i>
                                                <span class="currency">$</span>{{ formatCurrency(Math.abs(adst.netChange)) }}
                                            </div>
                                            <div class="net-label" :class="getNetChangeClass(adst.netChange)">
                                                {{ getNetChangeLabel(adst.netChange) }}
                                            </div>
                                        </div>
                                    </td>
                                    <td class="date-cell">
                                        <div class="date-container">
                                            <div class="date-value">{{ formatDate(adst.updatedAtFormatted1) }}</div>
                                            <div class="time-value">{{ formatTime(adst.updatedAtFormatted1) }}</div>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="!loading && !qtyAdjustments.length" class="empty-state">
                        <div class="empty-icon">
                            <i class="fas fa-chart-line"></i>
                        </div>
                        <h4>No Quantity Adjustments Found</h4>
                        <p>Start by creating your first quantity adjustment to track inventory changes.</p>
                        <AllowPermission :permission="PERMISSION_CODES.CMS_CREATE_ADJUSTMENT">
                            <router-link to="/inventory-adjustments/quantity/create">
                                <Button class="mt-3">
                                    <i class="fas fa-plus mr-2"></i>Create First Adjustment
                                </Button>
                            </router-link>
                        </AllowPermission>
                    </div>
                </div>
                <Paginator @change="updatePage" :page="page" :totalPages="totalPages" />
            </AllowPermission>
        </div>
    </Card>
</template>

<script>
import { mapState } from "vuex"
import CustomInput from "../../../components/crud/custom-input.vue"
import SelectDropdown from "../../../components/crud/SelectDropdown.vue"
import CustomTable from "../../../components/layout/CustomTable.vue"
import ProductSearchInput from "../../../components/product/ProductSearchInput.vue"
import Button from "../../../components/UI/Button.vue"
import Card from "../../../components/UI/Card.vue"
import QuantityFetchApi from "../../../utils/fetch/quantity-fetch-api"
import { PERMISSION_CODES } from "../../../store/permission"
import AllowPermission from "../../../components/layout/AllowPermission.vue"
import LinkComponent from "../../../components/UI/LinkComponent.vue"
import Paginator from "../../../components/other/Paginator.vue"
import AdjustmentTabs from "../../../components/inventory/AdjustmentTabs.vue"

export default {
    data() {
        return {
            search: "",
            page: 1,
            limit: 10,
            totalPages: 0,
            flushing: false,
            loading: false,
            qtyAdjustments: [],
            PERMISSION_CODES,
        }
    },
    components: {
        Paginator,
        AdjustmentTabs,
        LinkComponent,
        AllowPermission,
        Card,
        CustomInput,
        Button,
        SelectDropdown,
        CustomTable,
        ProductSearchInput,
    },
    async mounted() {
        await this.fetchQtyAdjustments()
    },
    methods: {
        async fetchQtyAdjustments() {
            try {
                this.loading = true
                const data = await QuantityFetchApi.getQuantityAdjustments({
                    page: this.page,
                    limit: this.limit,
                    search: this.search
                })
                this.qtyAdjustments = data.result
                this.totalPages = data.totalPages
            } catch (err) {
                console.log("error in fetching quantity adjustments", err)
                this.$toast.error("Failed to fetch quantity adjustments")
            }
            this.loading = false
        },
        async flushData() {
            this.flushing = true
            try {
                await QuantityFetchApi.flushQuantityAdjustments()
                this.qtyAdjustments = []
                this.$toast.success("All quantity adjustments cleared")
            } catch (err) {
                console.log("error in flushing items", err)
                this.$toast.error("Failed to clear quantity adjustments")
            }
            this.flushing = false
        },
        updatePage(e) {
            if (this.totalPages > 0) {
                if (e > 0 && e <= this.totalPages) {
                    this.page = e
                } else {
                    this.$toast.error("Invalid page!")
                }
            }
        },
        formatCurrency(value) {
            if (!value) return '0.00'
            return parseFloat(value).toFixed(2)
        },
        formatDate(dateString) {
            if (!dateString) return '-'
            try {
                const date = new Date(dateString)
                return date.toLocaleDateString('en-US', { 
                    month: 'short', 
                    day: 'numeric', 
                    year: 'numeric' 
                })
            } catch {
                return dateString.split(' ')[0] || '-'
            }
        },
        formatTime(dateString) {
            if (!dateString) return ''
            try {
                const date = new Date(dateString)
                return date.toLocaleTimeString('en-US', { 
                    hour: 'numeric', 
                    minute: '2-digit',
                    hour12: true 
                })
            } catch {
                const parts = dateString.split(' ')
                return parts.length > 1 ? parts.slice(1).join(' ') : ''
            }
        },
        getNetChangeClass(value) {
            if (value > 0) return 'positive'
            if (value < 0) return 'negative'
            return 'neutral'
        },
        getNetChangeIcon(value) {
            if (value > 0) return 'fas fa-arrow-up'
            if (value < 0) return 'fas fa-arrow-down'
            return 'fas fa-minus'
        },
        getNetChangeLabel(value) {
            if (value > 0) return 'Increase'
            if (value < 0) return 'Decrease'
            return 'No Change'
        },
    },
    watch: {
        page(e) {
            this.fetchQtyAdjustments()
        },
        search(e) {
            this.page = 1
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../../assets/variables.scss";

.qty-adjustment-setting {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 15px;
}

.loading-container {
    text-align: center;
    padding: 40px 20px;
    color: #666;
}

.adjustments-table-container { background: $lightCardBg; border-radius: $cardRadius; box-shadow: $cardShadow; overflow: hidden; margin-bottom: $sectionGap * 2; border: 1px solid $lightBorderColor; }

.custom-adjustments-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;

    thead {
        background: linear-gradient(135deg, #0D2D2C 0%, #1a4a47 100%);
        
        th {
            padding: 16px 12px;
            text-align: left;
            font-weight: 600;
            color: white;
            font-size: 0.85rem;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            border: none;
            position: relative;

            i {
                opacity: 0.8;
                font-size: 0.8rem;
            }

            &.adjustment-id-col {
                min-width: 220px;
            }

            &.items-col {
                min-width: 100px;
                text-align: center;
            }

            &.increase-col,
            &.decrease-col,
            &.net-change-col {
                min-width: 120px;
                text-align: center;
            }

            &.date-col {
                min-width: 140px;
                text-align: center;
            }
        }
    }

    tbody {
        tr.adjustment-row {
            border-bottom: 1px solid $lightBorderColor;
            transition: all 0.2s ease;

            &:hover {
                background-color: $lightHoverColor;
                transform: translateY(-1px);
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            }

            &:last-child {
                border-bottom: none;
            }
        }

        td { padding: $tableCellPaddingY $tableCellPaddingX; vertical-align: middle; border: none; }
    }
}

.adjustment-id-cell {
    .adjustment-link {
        text-decoration: none;
        color: inherit;
        display: block;

        &:hover {
            text-decoration: none;
            color: inherit;
        }
    }

    .adjustment-id-content {
        .adjustment-name {
            display: block;
            font-weight: 600;
            color: #0D2D2C;
            font-size: 0.95rem;
            margin-bottom: 2px;
        }

        .adjustment-type {
            display: block;
            font-size: 0.75rem;
            color: #BA8E58;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            font-weight: 500;
        }
    }
}

.items-cell {
    text-align: center;

    .items-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
        padding: 6px 12px;
        border-radius: 20px;
        font-weight: 600;
        color: #0D2D2C;
        font-size: 0.9rem;

        .items-text {
            font-size: 0.75rem;
            color: #666;
            font-weight: 400;
        }
    }
}

.increase-cell,
.decrease-cell {
    text-align: center;

    .cost-value {
        font-weight: 600;
        font-size: 0.9rem;
        padding: 4px 8px;
        border-radius: 6px;
        display: inline-block;
        min-width: 80px;

        .currency {
            font-size: 0.8rem;
            opacity: 0.8;
        }

        &.positive {
            color: #28a745;
            background-color: rgba(40, 167, 69, 0.1);
        }

        &.negative {
            color: #dc3545;
            background-color: rgba(220, 53, 69, 0.1);
        }

        &.neutral {
            color: #6c757d;
            background-color: #f8f9fa;
        }
    }
}

.net-change-cell {
    text-align: center;

    .net-change-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
    }

    .net-value {
        font-weight: 700;
        font-size: 0.95rem;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 6px 12px;
        border-radius: 8px;
        min-width: 90px;

        .currency {
            font-size: 0.85rem;
        }

        &.positive {
            color: #28a745;
            background: linear-gradient(135deg, rgba(40, 167, 69, 0.15) 0%, rgba(40, 167, 69, 0.05) 100%);
            border: 1px solid rgba(40, 167, 69, 0.2);
        }

        &.negative {
            color: #dc3545;
            background: linear-gradient(135deg, rgba(220, 53, 69, 0.15) 0%, rgba(220, 53, 69, 0.05) 100%);
            border: 1px solid rgba(220, 53, 69, 0.2);
        }

        &.neutral {
            color: #6c757d;
            background: #f8f9fa;
            border: 1px solid #e9ecef;
        }

        i {
            font-size: 0.8rem;
        }
    }

    .net-label {
        font-size: 0.7rem;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;

        &.positive {
            color: #28a745;
        }

        &.negative {
            color: #dc3545;
        }

        &.neutral {
            color: #6c757d;
        }
    }
}

.date-cell {
    text-align: center;

    .date-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 2px;
    }

    .date-value {
        font-weight: 600;
        color: #0D2D2C;
        font-size: 0.85rem;
    }

    .time-value {
        font-size: 0.75rem;
        color: #BA8E58;
        font-weight: 500;
    }
}

.empty-state {
    text-align: center;
    padding: 60px 20px;
    color: #6c757d;

    .empty-icon {
        margin-bottom: 20px;

        i {
            font-size: 4rem;
            color: #dee2e6;
        }
    }

    h4 {
        color: #0D2D2C;
        margin-bottom: 12px;
        font-weight: 600;
    }

    p {
        margin-bottom: 20px;
        color: #6c757d;
        max-width: 400px;
        margin-left: auto;
        margin-right: auto;
    }
}

/* Dark theme overrides */
.dark .adjustments-table-container { background: $darkCardBg; }
.dark .custom-adjustments-table thead { background: linear-gradient(135deg, $primaryColorDark 0%, darken($primaryColorDark, 8%) 100%); }
.dark .custom-adjustments-table tbody tr.adjustment-row { border-bottom-color: $darkBorderColor; }
.dark .custom-adjustments-table tbody tr.adjustment-row:hover { background: lighten($darkCardBg, 3%); }
.dark .adjustment-id-content .adjustment-name { color: $darkTextColor; }
.dark .items-badge .items-text { color: rgba($darkTextColor, 0.75); }
.dark .date-cell .date-value { color: $darkTextColor; }
.dark .date-cell .time-value { color: $secondaryColorDark; }
.dark .empty-state { color: rgba($darkTextColor, 0.7); }
.dark .empty-state .empty-icon i { color: rgba($darkTextColor, 0.25); }
.dark .empty-state h4 { color: $darkTextColor; }
.dark .empty-state p { color: rgba($darkTextColor, 0.75); }

// Responsive design
@media (max-width: 1200px) {
    .custom-adjustments-table {
        font-size: 0.85rem;

        thead th {
            padding: 12px 8px;
            font-size: 0.8rem;
        }

        tbody td {
            padding: 12px 8px;
        }
    }
}

@media (max-width: 768px) {
    .adjustments-table-container {
        margin: 0 -10px 20px -10px;
        border-radius: 0;
    }

    .custom-adjustments-table {
        font-size: 0.8rem;

        thead th {
            padding: 10px 6px;
            font-size: 0.75rem;

            i {
                display: none;
            }
        }

        tbody td {
            padding: 10px 6px;
        }

        .adjustment-id-content {
            .adjustment-name {
                font-size: 0.85rem;
            }

            .adjustment-type {
                font-size: 0.7rem;
            }
        }

        .items-badge {
            padding: 4px 8px;
            font-size: 0.8rem;

            .items-text {
                display: none;
            }
        }

        .net-change-container {
            .net-label {
                display: none;
            }
        }

        .date-container {
            .time-value {
                display: none;
            }
        }
    }
}
</style>
